import { useState } from "react";
import { Mail, ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewsletterSignup = ({ variant = "default" }) => {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        email,
        first_name: firstName || null,
      });
      setSubscribed(true);
      toast.success("Successfully subscribed to our newsletter!");
      setEmail("");
      setFirstName("");
    } catch (error) {
      if (error.response?.data?.detail === "Email already subscribed") {
        toast.info("You're already subscribed!");
      } else {
        toast.error("Unable to subscribe. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (subscribed) {
    return (
      <div className={`flex items-center gap-3 ${variant === "dark" ? "text-white" : "text-slate-700"}`} data-testid="newsletter-success">
        <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
          <Check className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="font-medium">Thank you for subscribing!</p>
          <p className={`text-sm ${variant === "dark" ? "text-slate-400" : "text-slate-500"}`}>
            You'll receive our latest updates.
          </p>
        </div>
      </div>
    );
  }

  if (variant === "dark") {
    // Footer version
    return (
      <form onSubmit={handleSubscribe} className="space-y-3" data-testid="newsletter-form-dark">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              required
              data-testid="newsletter-email-input-dark"
            />
          </div>
          <Button 
            type="submit" 
            disabled={loading}
            className="bg-amber-600 hover:bg-amber-700 text-white px-4"
            data-testid="newsletter-submit-btn-dark"
          >
            {loading ? "..." : <ArrowRight className="w-4 h-4" />}
          </Button>
        </div>
      </form>
    );
  }

  // Default/light version for homepage
  return (
    <form onSubmit={handleSubscribe} className="space-y-4" data-testid="newsletter-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input
          type="text"
          placeholder="First name (optional)"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          className="h-12"
          data-testid="newsletter-name-input"
        />
        <Input
          type="email"
          placeholder="Your email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-12"
          required
          data-testid="newsletter-email-input"
        />
      </div>
      <Button 
        type="submit" 
        disabled={loading}
        className="w-full md:w-auto bg-amber-700 hover:bg-amber-800 text-white px-8 py-3 font-sans font-bold uppercase text-sm"
        data-testid="newsletter-submit-btn"
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            Subscribing...
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Subscribe to Newsletter
          </span>
        )}
      </Button>
    </form>
  );
};

export default NewsletterSignup;
